import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { isNormalGame, useMatch } from "@/features/matches";
import { useApiErrorMessage } from "@/shared/api";
import { Skeleton } from "@/shared/ui/skeleton";

import type { AnalysisSection } from "../../model/AnalysisSection";
import { useAnalysisEvents } from "../../realtime/useAnalysisEvents";
import { BaselineLine, MatchHeader } from "../MatchHeader";
import { StartAnalysisButton } from "../StartAnalysisButton";
import { AnalysisBody } from "./AnalysisBody";

const NOT_ANALYZED = msg({
  id: "analysis.not_started.text",
  message: "This match hasn't been analyzed yet.",
});
const START = msg({ id: "analysis.not_started.start", message: "Analyze the match" });
const UNSUPPORTED_TITLE = msg({
  id: "analysis.unsupported_game_mode.title",
  message: "No analysis for this mode",
});
const UNSUPPORTED_TEXT = msg({
  id: "analysis.unsupported_game_mode.text",
  message:
    "We only analyze regular 6v6 matches. Street Brawl and other modes are played on a different map and in a different rhythm, so our checks and comparisons with strong players don't apply to them.",
});

type AnalysisPageProps = {
  matchId: number;
  // Открытая секция готового разбора — из search params маршрута (`analysisSearchSchema`).
  section?: AnalysisSection | undefined;
  onSectionChange: (section: AnalysisSection) => void;
};

// Экран разбора матча по макету design/prototype/Main.dc.html: шапка матча и состояние разбора.
// Street Brawl и другие режимы — объяснение вместо разбора, даже если разбор сделали раньше.
export const AnalysisPage = ({ matchId, section, onSectionChange }: AnalysisPageProps) => {
  const { _ } = useLingui();
  useAnalysisEvents();
  const match = useMatch(matchId);
  const error = useApiErrorMessage(match.error);

  return (
    <main
      data-page="match"
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-5 px-6 pt-6 pb-16"
    >
      {match.isPending && <Skeleton className="h-24" />}
      {match.isError && (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      )}
      {match.isSuccess && (
        <>
          <MatchHeader match={match.data}>
            {isNormalGame(match.data) && match.data.analysis_id !== null && (
              <BaselineLine analysisId={match.data.analysis_id} heroId={match.data.hero_id} />
            )}
          </MatchHeader>
          {!isNormalGame(match.data) ? (
            <section
              data-state="unsupported-game-mode"
              className="flex flex-col gap-2 rounded-xl border border-warn/40 bg-warn/10 px-4.5 py-4"
            >
              <h2 className="font-semibold text-warn">{_(UNSUPPORTED_TITLE)}</h2>
              <p className="max-w-[72ch] text-foreground-soft">{_(UNSUPPORTED_TEXT)}</p>
            </section>
          ) : match.data.analysis_id === null ? (
            <section className="flex flex-col gap-3 rounded-xl border border-border bg-surface px-4.5 py-4">
              <p className="text-foreground-soft">{_(NOT_ANALYZED)}</p>
              <StartAnalysisButton matchId={matchId} label={START} />
            </section>
          ) : (
            <AnalysisBody
              match={match.data}
              analysisId={match.data.analysis_id}
              section={section}
              onSectionChange={onSectionChange}
            />
          )}
        </>
      )}
    </main>
  );
};
